/**
 * Os dois tetos mensais — o freio de gasto.
 *
 * Moram no engine porque servidor e painel precisam da **mesma** resposta: o servidor para recusar a
 * config, o painel para mostrar quanto falta. Duas regras divergiriam, e o sintoma seria um painel
 * dizendo "dentro do limite" enquanto o widget some do site do cliente.
 *
 * A janela é o mês em **UTC**, igual ao dia do analytics (`analyticsDayKey`). Fuso local daria uma
 * virada diferente da que o painel soma, e os dois números precisam bater.
 *
 * ## Por que são dois
 *
 * **Conversa** é fluxo iniciado. **Requisição** é carregamento de página — a config é buscada antes de
 * o botão flutuante existir, porque ele precisa da cor e da imagem.
 *
 * O teto de conversas nasceu primeiro e não é um freio de gasto: o custo vem das requisições, e um pico
 * de page views sem conversa nenhuma passa inteiro por ele. Quem paga a conta é a requisição; quem o
 * teto de conversas mede é outra coisa. Daí o segundo teto.
 *
 * Os dois convivem porque medem coisas que o dono quer limitar por razões diferentes: conversa é
 * volume de atendimento, requisição é volume de tráfego.
 */
/**
 * Teto de um webchat que nunca teve limite escolhido.
 *
 * Existe ligado por padrão de propósito: um webchat sem teto é uma conta aberta, e o custo de errar
 * para cima (widget some, dono ajusta em dois cliques) é bem menor que o de errar para baixo.
 */
export const DEFAULT_CONVERSATION_LIMIT = 10000;
/**
 * Resolve o teto efetivo. Devolve `null` quando é ilimitado.
 *
 * Os três estados são distintos de propósito:
 * - **ausente** (`undefined`): nunca foi escolhido → vale o padrão;
 * - **`null`**: o dono desligou o teto conscientemente;
 * - **número**: o teto que ele escolheu. Zero e negativo viram zero, que barra tudo — é um valor
 *   estranho de escolher, mas respeitá-lo é melhor que reinterpretá-lo como "ilimitado".
 */
export function resolveConversationLimit(value) {
    if (value === null)
        return null;
    if (value === undefined)
        return DEFAULT_CONVERSATION_LIMIT;
    if (!Number.isFinite(value))
        return DEFAULT_CONVERSATION_LIMIT;
    return Math.max(0, Math.trunc(value));
}
/**
 * Resolve o teto de **requisições**. `null` = ilimitado.
 *
 * Diferente de `resolveConversationLimit` num ponto que importa: **ausente também é ilimitado**, e não
 * um padrão ligado.
 *
 * O de conversas vem ligado porque webchat sem escolha explícita é conta aberta, e errar para cima ali
 * custa pouco — o widget some e o dono ajusta em dois cliques. Um teto de requisições com padrão errado
 * faz o oposto: desliga um widget que estava funcionando, num site cujo tráfego a gente não conhece. E
 * não há número seguro a adivinhar, porque page views por conversa variam cem vezes entre um site e
 * outro.
 *
 * Então este é opt-in. Zero continua sendo zero — barra tudo —, porque respeitar um valor estranho é
 * melhor que reinterpretá-lo.
 */
export function resolveRequestLimit(value) {
    if (value === null || value === undefined)
        return null;
    if (!Number.isFinite(value))
        return null;
    return Math.max(0, Math.trunc(value));
}
/*
 * Aqui morava `REQUEST_COUNT_SAMPLE = 20`, a taxa de amostragem do contador de requisições.
 *
 * Existia porque contar exato custava uma escrita do Firestore **por requisição** — a operação mais cara
 * do caminho quente, a US$ 2,10 por milhão. Amostrar 1 em 20 e somar 20 derrubava o custo para ~US$ 0,11
 * ao preço de o número do painel ser estimativa.
 *
 * Sumiu quando a API saiu das Cloud Functions para uma VM: com processo longo, o `Store` acumula em
 * memória e descarrega com um `increment` a cada 30s. Contar exato passou a ser **mais barato** que
 * amostrar, e a estimativa deixou de ter qualquer justificativa.
 */
/** Chave do contador mensal, `AAAA-MM` em UTC. */
export function monthKey(timestamp) {
    const date = typeof timestamp === 'number' ? new Date(timestamp) : timestamp;
    return date.toISOString().slice(0, 7);
}
/**
 * Segundos até a virada do mês em UTC. Vira o `Retry-After` do 429: um teto mensal de fato se
 * renova, então dizer quando é mais útil que só recusar.
 */
export function secondsUntilNextMonth(timestamp) {
    const date = typeof timestamp === 'number' ? new Date(timestamp) : timestamp;
    const proximo = Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1);
    return Math.max(1, Math.ceil((proximo - date.getTime()) / 1000));
}
//# sourceMappingURL=quota.js.map